if (!customElements.get('pdp-complementary')) {
  customElements.define('pdp-complementary', class extends HTMLElement {
    static catalogs = new Map();

    connectedCallback() {
      this.onSubmit = this.onSubmit || this.addProduct.bind(this);
      this.onClick = this.onClick || this.selectTab.bind(this);
      this.onKeydown = this.onKeydown || this.navigateTabs.bind(this);
      this.onVariantChange = this.onVariantChange || ((event) => {
        if (event.target.matches(`form[id="product-form-${this.dataset.productId}"] input[name="id"]`) ||
            event.target.closest('variant-selects, variant-radios')) {
          queueMicrotask(() => this.updateKits());
        }
      });
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.productView = this.closest('.productView');
      this.productView?.addEventListener('change', this.onVariantChange);
      this.variants = JSON.parse(this.querySelector('[data-variant-skus]').textContent);
      this.hasComplementary = !!this.querySelector('.pdp-complementary__item');
      this.heading = this.querySelector('[data-complementary-heading]');
      this.headingText = this.heading.textContent;
      this.tablist = this.querySelector('[role="tablist"]');
      this.panels = [...this.querySelectorAll('[data-complementary-panel]')];
      this.tabs = [...this.tablist.querySelectorAll('[role="tab"]')];
      if (!this.dataset.kitsUrl || !this.variants.some(variant => variant.sku?.trim())) return;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.observer.disconnect();
          this.loadKits();
        }
      }, { rootMargin: '400px' });
      // The parent remains measurable when this block has no complementary products.
      this.observer.observe(this.parentElement);
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.productView?.removeEventListener('change', this.onVariantChange);
      this.observer?.disconnect();
    }

    async loadKits() {
      const catalogUrl = new URL(this.dataset.kitsUrl, location.origin).href;
      const cache = this.constructor.catalogs;
      if (!cache.has(catalogUrl)) {
        const request = (async () => {
          const products = new Map();
          const visited = new Set();
          let next = catalogUrl;
          while (next && !visited.has(next)) {
            const url = new URL(next, catalogUrl);
            if (url.origin !== location.origin) throw new Error('Invalid kits URL');
            url.searchParams.set('view', 'kit-matches');
            visited.add(url.href);
            // Request the Liquid view, not Shopify's native collection JSON response.
            const response = await fetch(url, { headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Kits unavailable');
            const page = await response.json();
            if (!Array.isArray(page.products)) throw new Error('Invalid kits response');
            page.products.forEach(product => products.set(String(product.id), product));
            next = page.next ? new URL(page.next, url).href : null;
          }
          return [...products.values()];
        })();
        cache.set(catalogUrl, request);
        request.catch(() => cache.delete(catalogUrl));
      }
      try {
        this.kits = await cache.get(catalogUrl);
        if (this.isConnected) this.updateKits();
      } catch {
        // Complementary products and the main purchase form remain usable.
        this.kits = [];
      }
    }

    updateKits() {
      if (!this.kits) return;
      const picker = this.productView?.querySelector('variant-selects, variant-radios');
      const input = document.getElementById(`product-form-${this.dataset.productId}`)?.querySelector('[name="id"]');
      const variantId = input?.value || this.dataset.variantId;
      const invalidSelection = picker && 'currentVariant' in picker && !picker.currentVariant;
      const sku = invalidSelection ? '' : this.variants.find(variant => variant.id === variantId)?.sku?.trim();
      const matches = sku ? this.kits.filter(kit => String(kit.id) !== this.dataset.productId &&
        Array.isArray(kit.skuComponents) && kit.skuComponents.some(component => typeof component === 'string' && component.trim() === sku)) : [];
      const list = this.querySelector('[data-kit-list]');
      const focusedKitLink = list.contains(document.activeElement);
      const kitLabel = matches.some(kit => kit.compareAtPrice && kit.savings) ? this.dataset.kitsDiscountTitle : this.dataset.kitsTitle;
      const hadKitsTab = !this.tablist.hidden;
      const activePanel = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.dataset.panel;
      list.replaceChildren(...matches.map(kit => this.createKitCard(kit)));
      this.hidden = !this.hasComplementary && !matches.length;
      this.tablist.hidden = !this.hasComplementary || !matches.length;
      this.heading.hidden = !this.tablist.hidden;
      this.heading.textContent = this.hasComplementary ? this.headingText : kitLabel;
      this.tabs[1].textContent = kitLabel;
      this.panels.forEach((panel, index) => {
        if (!this.tablist.hidden) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.tabs[index].id);
          panel.tabIndex = 0;
        } else {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
          panel.removeAttribute('tabindex');
        }
      });
      if (!this.tablist.hidden) {
        this.activatePanel(hadKitsTab ? activePanel : 'combine');
      } else {
        this.panels[0].hidden = !this.hasComplementary;
        this.panels[1].hidden = !matches.length;
      }
      if (focusedKitLink || (this.tablist.hidden && this.tablist.contains(document.activeElement))) {
        const target = this.hidden ? input?.form?.querySelector('[type="submit"]') :
          this.querySelector('[data-complementary-panel]:not([hidden]) a');
        target?.focus({ preventScroll: true });
      }
    }

    createKitCard(kit) {
      const card = this.querySelector('[data-kit-card]').content.firstElementChild.cloneNode(true);
      card.querySelectorAll('[data-kit-link]').forEach(link => { link.href = kit.url; });
      card.querySelector('[data-kit-title]').textContent = kit.title;
      const image = card.querySelector('[data-kit-image]');
      if (kit.image) image.src = kit.image;
      else image.remove();
      card.querySelector('[data-kit-price]').textContent = kit.price;
      if (kit.compareAtPrice && kit.savings) {
        card.querySelector('[data-kit-saving]').hidden = false;
        card.querySelector('[data-kit-savings]').textContent = kit.savings;
        card.querySelector('[data-kit-percent]').textContent = `(${kit.discountPercent}%)`;
        card.querySelector('[data-kit-percent]').hidden = !(kit.discountPercent > 0);
        const compare = card.querySelector('[data-kit-compare]');
        compare.hidden = false;
        compare.textContent = kit.compareAtPrice;
      }
      card.querySelector('[data-kit-available]').hidden = !kit.available;
      card.querySelector('[data-kit-unavailable]').hidden = !!kit.available;
      return card;
    }

    activatePanel(name, focus = false) {
      this.tabs.forEach(tab => {
        const selected = tab.dataset.panel === name;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.complementaryPanel !== name; });
    }

    selectTab(event) {
      const tab = event.target.closest('[role="tab"]');
      if (tab && this.tablist.contains(tab)) this.activatePanel(tab.dataset.panel);
    }

    navigateTabs(event) {
      const index = this.tabs.indexOf(event.target);
      if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 :
        (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
      this.activatePanel(this.tabs[next].dataset.panel, true);
    }

    async addProduct(event) {
      const form = event.target.closest('.pdp-complementary__form');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      if (button.disabled) return;
      const status = form.closest('.pdp-complementary__item').querySelector('[role="status"]');
      status.hidden = true;
      button.disabled = true;
      form.setAttribute('aria-busy', 'true');

      try {
        const action = new URL(form.action);
        action.pathname = action.pathname.replace(/\/$/, '') + '.js';
        const body = new FormData(form);
        body.cuffCartSerialized = true;
        const response = await fetch(action, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        }
        status.textContent = this.dataset.success;
        status.hidden = false;
        await window.CuffCart.open();
      } catch (error) {
        status.textContent = error.message || this.dataset.error;
        status.hidden = false;
      } finally {
        button.disabled = false;
        form.removeAttribute('aria-busy');
      }
    }
  });
}
